// File Name: pages/match-pages/JoinMatchPage.js
//
// /join — enter a match code, give a name and email, and join the match. The
// visitor appears in the host's lobby exactly as an in-app player does.
//
// ⚠️ The join itself is performed by the Worker's /guest-join endpoint, NOT by
// writing to Firestore from here. Three reasons, all load-bearing:
//
//   * the host's "new player joined" notification lives under
//     `users/{adminUid}/notifications`, which rules gate on being signed in — a
//     browser-side join would add the player and tell nobody;
//   * the email is resolved to an ACCOUNT, and creating one needs admin
//     credentials that can never sit in a page anyone can view-source;
//   * the join writes several documents that must land together, and it has to
//     match `MatchRepository.joinMatch` field for field. One implementation,
//     server-side, is the only way that stays true.
//
// What the email does — the server decides, this page only reports it:
//
//   * an account already exists -> the visitor joins as themselves, so the
//     match counts towards their real stats;
//   * no account exists         -> one is created for them, and they are signed
//     in silently with the custom token the Worker hands back.
//
// Watching without joining is still supported — "just watching" goes straight
// to /live.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { signInSilently } from '../../lib/ynoMatchAuth';
import { findMatchByCode, normaliseCode, sideForCode } from '../../lib/ynoMatchDb';
import { JOIN_SERVICE_URL } from './matchConfig';
import { useMatchLang } from './matchI18n';
import { GetAppCard, MatchShell } from './MatchShell';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Is [code] one of this match's three codes?
 *
 * Mirrors `MatchRepository.findByCode`'s field order. Used to avoid re-fetching
 * a preview the page already has — and, more importantly, to DROP a stale
 * preview: without this, editing the code after a successful lookup would leave
 * the previous match's "you will join Team A" note sitting under the new code.
 */
const matchHasCode = (m, code) =>
  !!m && (m.code === code || m.codeA === code || m.codeB === code);

export default function JoinMatchPage() {
  const { lang, t, toggle } = useMatchLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { code: codeParam } = useParams();

  const referral = params.get('ref');
  const initialCode = normaliseCode(
    params.get('code') || params.get('c') || codeParam || ''
  );

  const [code, setCode] = useState(initialCode);
  const [match, setMatch] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const [looking, setLooking] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  // { pending, teamName, isNewAccount, already }
  const [result, setResult] = useState(null);
  const attempted = useRef(false);

  // ---- the match preview --------------------------------------------------
  //
  // Resolving the code is no longer a step the visitor has to complete — the
  // form is one card and one button. This only fetches the preview line
  // ("Falcons vs Eagles · you will join Team A"), so it fails QUIETLY: the
  // Worker validates the code on submit and is the only authority on it.
  // Surfacing a lookup error here would put a red message under a code the
  // visitor is still typing.
  const lookup = useCallback(async (raw, { quiet = true } = {}) => {
    const c = normaliseCode(raw);
    if (!c) return;
    if (!quiet) setLooking(true);
    try {
      const found = await findMatchByCode(c);
      setMatch(found || null);
    } catch (e) {
      setMatch(null);
    } finally {
      if (!quiet) setLooking(false);
    }
  }, []);

  // A code in the link resolves on its own — the visitor tapped a share link
  // and should not have to press anything to see which match it is.
  useEffect(() => {
    if (attempted.current || referral || !initialCode) return;
    attempted.current = true;
    lookup(initialCode, { quiet: false });
  }, [initialCode, referral, lookup]);

  // A typed code gets the same preview once the field loses focus. Cheap, and
  // it keeps "which team am I joining?" answerable for someone who did not
  // arrive on a share link.
  const previewOnBlur = () => {
    const c = normaliseCode(code);
    if (c && (!match || !matchHasCode(match, c))) lookup(c);
  };

  // ---- join ---------------------------------------------------------------
  const join = async (e) => {
    e.preventDefault();
    if (joining) return;

    // Order matters: the code is what the visitor came here with, so an empty
    // one is the first thing to say.
    if (!normaliseCode(code)) {
      setError(t('enterMatchCode'));
      return;
    }
    if (name.trim().length < 2) {
      setError(t('enterYourName'));
      return;
    }
    // The email is REQUIRED now, not optional as it was when this page only
    // created accountless guests. It is what decides whether the visitor joins
    // as their existing self or gets an account made for them, so a blank one
    // has no sensible behaviour to fall back to.
    if (!email.trim()) {
      setError(t('enterYourEmail'));
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError(t('enterValidEmail'));
      return;
    }

    setJoining(true);
    setError('');
    try {
      const res = await fetch(`${JOIN_SERVICE_URL}/guest-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: normaliseCode(code),
          name: name.trim(),
          email: email.trim(),
        }),
      });
      const body = await res.json().catch(() => null);

      if (!res.ok || !body || body.ok !== true) {
        const key = body && body.error;
        setError(
          key === 'match_over'
            ? t('matchOverJoin')
            : key === 'no_match'
            ? t('noMatchForCode')
            : key === 'invalid_name'
            ? t('enterYourName')
            : key === 'invalid_email'
            ? t('enterValidEmail')
            : t('couldNotJoin')
        );
        setJoining(false);
        return;
      }

      // Sign in silently when the server created an account for this visitor.
      // Awaited so the session exists before the confirmation renders, but it
      // can only ever resolve — signInSilently swallows its own failures,
      // because the join is already written and a missing session is cosmetic.
      if (body.customToken) await signInSilently(body.customToken);

      setResult({
        pending: body.pending === true,
        teamName: body.teamName,
        isNewAccount: body.isNewAccount === true,
        already: body.already === true,
      });
      setJoining(false);
    } catch (_) {
      setError(t('couldNotJoin'));
      setJoining(false);
    }
  };

  // Watching still needs a code — /live with an empty one is the bare
  // "No match selected" screen, which reads as a broken button.
  const goLive = () => {
    const c = normaliseCode(code);
    if (!c) {
      setError(t('enterMatchCode'));
      return;
    }
    navigate(`/live?code=${encodeURIComponent(c)}`);
  };

  const teamName = (m) => {
    if (!m) return '';
    return sideForCode(m, code) === 'b'
        ? m.teamBName || 'Team B'
        : m.teamAName || 'Team A';
  };

  // ---- referral landing ---------------------------------------------------
  if (referral) {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <h1>{t('refWelcome')}</h1>
        <p className="sub">{t('refGetApp')}</p>
        <div className="card" style={{ marginTop: 20 }}>
          <p className="eyebrow">{t('refCodeLabel')}</p>
          <div
            className="ltr"
            style={{
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: 5,
              color: 'var(--volt)',
              fontFamily: "'Barlow Condensed', 'Cairo', sans-serif",
            }}
          >
            {normaliseCode(referral)}
          </div>
          <p className="sub" style={{ marginTop: 10, fontSize: 13.5 }}>
            {t('refEnterOnSignup')}
          </p>
        </div>
        <GetAppCard t={t} />
      </MatchShell>
    );
  }

  // ---- joined -------------------------------------------------------------
  if (result) {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <div className="card center">
          <div style={{ fontSize: 44, lineHeight: 1.2 }}>
            {result.pending ? '⏳' : '✅'}
          </div>
          <h1 style={{ marginTop: 12 }}>
            {result.pending
              ? t('pendingTitle')
              : result.already
              ? t('alreadyInTitle')
              : t('joinedTitle')}
          </h1>
          <p className="sub">
            {result.pending
              ? t('pendingBody')
              : result.already
              ? t('alreadyInBody')
              : t('joinedBody')}
          </p>
          {!result.pending && result.teamName ? (
            <div className="note" style={{ textAlign: 'start' }}>
              {t('youWillJoinTeam')} <b>{result.teamName}</b>
            </div>
          ) : null}
          {/* Which account they ended up on. A brand-new account is worth
              saying out loud — it is the thing they can claim later, and
              nothing else on the page would tell them it exists. */}
          {!result.already ? (
            <p className="sub" style={{ marginTop: 12, fontSize: 12.5 }}>
              {result.isNewAccount ? t('accountCreatedNote') : t('signedInNote')}
            </p>
          ) : null}
          <button
            className="primary"
            type="button"
            onClick={goLive}
            style={{ marginTop: 16 }}
          >
            {t('watchLive')}
          </button>
        </div>
        <GetAppCard t={t} />
      </MatchShell>
    );
  }

  // ---- looking ------------------------------------------------------------
  if (looking) {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <div className="card center">
          <div className="spinner" />
          <p className="sub">{t('checking')}</p>
        </div>
      </MatchShell>
    );
  }

  // ---- the form -----------------------------------------------------------
  //
  // ONE card, ONE button. This replaced a two-step form where the name and
  // email were hidden until the code resolved — which read, correctly, as
  // "the page is only asking for a code".
  return (
    <MatchShell t={t} lang={lang} toggle={toggle}>
      {!match ? (
        <>
          <h1>{t('landingTitle')}</h1>
          <p className="sub">{t('landingSub')}</p>
        </>
      ) : null}

      {/* One <form>, so the keyboard's enter key submits from ANY field. In the
          app this exact omission was client bug C18, where enter did nothing at
          all. */}
      <form onSubmit={join} autoComplete="on">
        <div className="card" style={{ marginTop: match ? 0 : 20 }}>
          <p className="eyebrow">{t('haveCode')}</p>
          <input
            className="ltr"
            name="code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              // Drop a preview the moment it stops describing what is typed,
              // or the old match's "you will join Team A" note would sit under
              // a completely different code.
              if (match && !matchHasCode(match, normaliseCode(e.target.value))) {
                setMatch(null);
              }
            }}
            onBlur={previewOnBlur}
            placeholder={t('codeHint')}
            aria-label={t('matchCode')}
            maxLength={12}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />

          {/* Only rendered once the code has resolved. It is a preview, not a
              gate — nothing below waits for it. */}
          {match ? (
            <div className="note" style={{ textAlign: 'start' }}>
              <b>
                {match.teamAName || 'Team A'} {t('vs')}{' '}
                {match.teamBName || 'Team B'}
              </b>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--dim)' }}>
                {t('youWillJoinTeam')} {teamName(match)}
                {match.status === 'live' ? ` · ${t('matchStartedApprove')}` : ''}
              </div>
            </div>
          ) : null}

          <p className="eyebrow" style={{ marginTop: 16 }}>
            {t('yourName')}
          </p>
          <input
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('fullName')}
            aria-label={t('yourName')}
            maxLength={60}
            autoCapitalize="words"
            style={{ letterSpacing: 0, textTransform: 'none' }}
          />

          <p className="eyebrow" style={{ marginTop: 16 }}>
            {t('emailLabel')}
          </p>
          <input
            className="ltr"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            aria-label={t('emailLabel')}
            maxLength={254}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            style={{ letterSpacing: 0, textTransform: 'none' }}
          />
          {/* ⚠️ The explanatory line under this field ('We use it to find your
              YNO account — or create one for you…') was REMOVED 2026-08-21 at
              the user's request. It was the ONLY place the visitor was told,
              before submitting, that an account may be created for their
              address. The confirmation still says so afterwards
              (`accountCreatedNote`). See §63c. */}

          {error ? <p className="err">{error}</p> : null}

          <button
            className="primary"
            type="submit"
            disabled={joining}
            style={{ marginTop: 16 }}
          >
            {joining ? t('joining') : t('joinTheMatch')}
          </button>
          {/* ⚠️ The "Just watching? Skip this" secondary button was REMOVED
              2026-08-21 at the user's request. It was the only entrance to
              /live for someone who does not join — the app never links there
              (see lib/links.dart) and nothing else on the site does either.
              /live is now reachable only after joining, via `watchLive` on the
              confirmation, or by typing the URL. Deliberate; see §63b. */}
        </div>
      </form>

      <GetAppCard t={t} />
    </MatchShell>
  );
}
